import { useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const inputSt = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: '12px', padding: '12px 14px', color: 'var(--text)', fontFamily: 'Plus Jakarta Sans',
  fontSize: '16px', outline: 'none', letterSpacing: '4px', textAlign: 'center',
};
const primaryBtn = (disabled) => ({
  width: '100%', padding: '13px', borderRadius: '25px', border: 'none',
  background: disabled ? 'var(--surface3)' : 'linear-gradient(135deg, var(--purple), #7c3aed)',
  color: disabled ? 'var(--text-dim)' : 'white', fontFamily: 'Nunito', fontWeight: 700, fontSize: '14px',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

// Shows a list of one-time backup codes with a copy button
export function BackupCodes({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(codes.join('\n')); setCopied(true); } catch {}
  };
  return (
    <div>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '12px' }}>
        Save these backup codes somewhere safe. Each code works once if you lose access to your authenticator app. They won't be shown again.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', fontFamily: 'monospace', fontSize: '14px', color: 'var(--text)', marginBottom: '12px' }}>
        {codes.map(c => <div key={c} style={{ textAlign: 'center', padding: '4px 0' }}>{c}</div>)}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={copy} style={{ ...primaryBtn(false), background: 'var(--surface3)', color: 'var(--text)' }}>{copied ? 'Copied ✓' : 'Copy codes'}</button>
        <button onClick={onDone} style={primaryBtn(false)}>I saved them</button>
      </div>
    </div>
  );
}

// Guided setup: QR code -> confirm code -> backup codes
export default function TwoFactorSetup({ onEnabled }) {
  const [step, setStep] = useState('start');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.post('/api/auth/2fa/setup', {}, auth());
      setSecret(res.data.secret);
      setQr(await QRCode.toDataURL(res.data.uri, { margin: 1, width: 200 }));
      setStep('scan');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start setup.');
    } finally { setBusy(false); }
  };

  const confirm = async (value = code) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post('/api/auth/2fa/enable', { code: value }, auth());
      setCodes(res.data.backup_codes);
      setStep('codes');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code.');
    } finally { setBusy(false); }
  };

  if (step === 'start') {
    return (
      <div>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '12px' }}>
          Protect your account with an authenticator app such as Google Authenticator, Aegis or 1Password. After your password, BLOBL will ask for a 6-digit code.
        </div>
        {error && <div style={{ color: '#f43f5e', fontSize: '13px', marginBottom: '10px' }}>{error}</div>}
        <button onClick={start} disabled={busy} style={primaryBtn(busy)}>{busy ? '…' : 'Set up two-factor authentication'}</button>
      </div>
    );
  }

  if (step === 'scan') {
    return (
      <div>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '12px' }}>
          1. Scan this QR code with your authenticator app.<br />2. Enter the 6-digit code it shows.
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
          <img src={qr} alt="QR code for your authenticator app" width={200} height={200} style={{ borderRadius: '12px', background: 'white' }} />
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-dim)', textAlign: 'center', marginBottom: '14px', wordBreak: 'break-all' }}>
          Can't scan? Enter this key: <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{secret}</span>
        </div>
        <input
          value={code}
          onChange={e => {
            const next = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCode(next);
            setError('');
            if (next.length === 6) confirm(next);
          }}
          onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) confirm(); }}
          inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
          style={{ ...inputSt, marginBottom: '10px' }}
        />
        {error && <div style={{ color: '#f43f5e', fontSize: '13px', marginBottom: '10px' }}>{error}</div>}
        <button onClick={() => confirm()} disabled={busy || code.length !== 6} style={primaryBtn(busy || code.length !== 6)}>{busy ? '…' : 'Enable'}</button>
      </div>
    );
  }

  return <BackupCodes codes={codes} onDone={onEnabled} />;
}

export { inputSt as codeInputStyle, primaryBtn };
